import type { Logger } from "@expert/logging";
import type { Device, ExternalDevice } from "@expert/shared-types";

import { type DeviceInfoOrigin, useDeviceInfoStore } from "./store";

interface SetDeviceInfoArgs {
    sessionId: string;
    externalDevices?: ExternalDevice[] | null;
    detectedDevices?: Device[] | null;
    origin: DeviceInfoOrigin;
}

/**
 * Stores the device GAIA attached to a reactive response.
 *
 * Logs scalars only, never the arrays: in `remote-expert*` a serialized array exceeds `ignore_above`
 * and drops out of aggregations, making populated payloads look absent.
 */
export const setDeviceInfo = (
    { sessionId, externalDevices, detectedDevices, origin }: SetDeviceInfoArgs,
    logger: Logger,
) => {
    const external = externalDevices ?? [];
    const detected = detectedDevices ?? [];

    // Bailing means an empty probe writes no `probe` entry, so it can't lock out later incidental data.
    if (!external.length && !detected.length) {
        return;
    }

    useDeviceInfoStore.getState().setDevices(
        sessionId,
        {
            ...(external.length && { externalDevices: external }),
            ...(detected.length && { detectedDevices: detected }),
        },
        origin,
    );

    const primary = external[0];
    const fallback = detected[0];
    logger.child({ module: "setDeviceInfo" }).info(
        {
            sessionId,
            deviceOrigin: origin,
            externalDeviceCount: external.length,
            detectedDeviceCount: detected.length,
            deviceName: primary?.name ?? fallback?.name,
            deviceOem: primary?.oem ?? fallback?.oem,
            deviceModel: primary?.model ?? fallback?.model,
            deviceOsType: primary?.osType ?? fallback?.osType,
            deviceSource: primary ? "ozmo" : "asurion",
        },
        "Device info set",
    );
};
